import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'ui';
import Badge from 'ui/Badge';
import { Battery } from '../Battery';

import { ObjectSensorType, ObjectZoneType, ZoneAiAnalysis } from 'entities/common';
import { getFilePresignedUrl, getZoneAiAnalysisLatest } from 'services/sensors';
import { QUERY_PARAMS, SensorMeasurement, SOIL_TYPE } from 'utils/constants';
import { formatLocalDateTime, parseServerDate } from 'utils/helpers';
import { PAGES } from 'utils/link';

import { ReactComponent as EmptySvg } from 'assets/images/icons/empty.svg';
import noImage from 'assets/images/no-image.png';
import { plantsApi, PlantSearchResult } from 'services/plants';

type ZoneStatus = 'Healthy' | 'Issue detected' | 'Needs attention' | 'No information';

const ZONE_METRIC_COLUMN_ORDER = [
  '4097', // Air Temperature
  '4098', // Air Humidity
  '4100', // CO2
  '4102', // Soil Temperature
  '4103', // Soil Moisture
  '4108', // Electrical Conductivity
  '4204', // Soil Pore Water Electrical Conductivity
  '4205', // Epsilon
];

const ZONE_METRIC_SHORT_LABELS: Record<string, string> = {
  '4097': 'Air temp',
  '4098': 'Air humid',
  '4100': 'CO\u2082',
  '4102': 'Soil temp',
  '4103': 'Soil moist',
  '4108': 'EC',
  '4204': 'Pore EC',
  '4205': 'Epsilon',
};

/** Sort priority for zone sensors: S2103 → S2104 → S2108 */
const ZONE_SENSOR_TYPE_ORDER: Record<string, number> = {
  sensecap_s2103: 0,
  sensecap_s2104: 1,
  sensecap_s2108: 2,
};

const STATUS_TO_BADGE: Record<ZoneStatus, 'error' | 'success' | 'warning' | 'info'> = {
  'Issue detected': 'error',
  Healthy: 'success',
  'Needs attention': 'warning',
  'No information': 'info',
};

const STATUS_TO_CONTAINER_CLASS: Record<ZoneStatus, string> = {
  'Issue detected': 'zone-error',
  Healthy: 'zone-healthy',
  'Needs attention': 'zone-warning',
  'No information': 'zone-info',
};

function computeZoneStatus(aiAnalysis: ZoneAiAnalysis | null): ZoneStatus {
  if (aiAnalysis) {
    const status = aiAnalysis.status?.toLowerCase();
    if (status === 'red') return 'Issue detected';
    if (status === 'yellow') return 'Needs attention';
    if (status === 'blue') return 'Healthy';
  }
  return 'No information';
}

function formatSnakeCase(value: string | null | undefined): string {
  if (!value) return '-';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function sensorTypeLabel(sensorType: string): string {
  const match = sensorType.match(/s(\d+)/i);
  return match ? `S${match[1]}` : sensorType;
}

function formatDate(dateStr: string): string {
  if (Number.isNaN(parseServerDate(dateStr).valueOf())) return '-';
  return formatLocalDateTime(dateStr);
}

function getLatestMetric(sensor: ObjectSensorType) {
  return (sensor?.active && sensor?.latest_metrics?.[0]) ?? null;
}

export function getSensorStatus(sensor: ObjectSensorType): { text: string; isOffline: boolean } {
  if (sensor.is_deleted) return { text: '-', isOffline: false };
  if (!sensor.active) return { text: 'Turned off', isOffline: true };
  const metric = getLatestMetric(sensor);
  if (!metric) return { text: '-', isOffline: false };
  if (metric.online_status == null) return { text: '-', isOffline: false };
  const online = !!metric.online_status;
  return { text: online ? 'Online' : 'Offline', isOffline: !online };
}

function getSensorBattery(sensor: ObjectSensorType): { text: string; isLow: boolean } {
  if (sensor.is_deleted || !sensor.active) return { text: '-', isLow: false };
  const metric = getLatestMetric(sensor);
  if (!metric) return { text: '-', isLow: false };
  if (metric.battery_status == null) return { text: '-', isLow: false };
  const high = !!metric.battery_status;
  return { text: high ? 'High' : 'Low', isLow: !high };
}

function getLastUpdate(sensor: ObjectSensorType): string {
  if (sensor.is_deleted) return 'Deleted';
  if (!sensor.active) return 'Turned off';
  const metric = getLatestMetric(sensor);
  if (!metric) return '-';
  return formatDate(metric.time);
}

function getMetricValue(sensor: ObjectSensorType, measurementId: string): string {
  const metric = getLatestMetric(sensor);
  if (!metric || typeof (metric?.points || []) === 'string') return '-';
  const point = (metric?.points || [])?.find((p) => String(p.measurement_id) === measurementId);
  if (!point) return '-';
  const def = SensorMeasurement[measurementId];
  const unit = def?.unit || '';
  return `${point.measurement_value}${unit ? ' ' + unit : ''}`;
}

type Props = {
  zone: ObjectZoneType;
};

export const ZoneCard: React.FC<Props> = ({ zone }) => {
  const navigate = useNavigate();
  const [zoneStatus, setZoneStatus] = useState<ZoneStatus>('No information');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [plantList, setPlantList] = useState<PlantSearchResult[]>([]);

  useEffect(() => {
    getZoneAiAnalysisLatest(zone.id)
      .then((res) => setZoneStatus(computeZoneStatus(res.data)))
      .catch(() => setZoneStatus('No information'));
  }, [zone.id]);

  useEffect(() => {
    if (zone.photo_file_id) {
      getFilePresignedUrl(zone.photo_file_id)
        .then((res) => {
          if (res.data?.url) setPhotoUrl(res.data.url);
        })
        .catch(() => {});
    }
  }, [zone.photo_file_id]);

  useEffect(() => {
    if (zone.plant_type) {
      plantsApi.list(zone.plant_type, zone?.plant_name || undefined).then((res) => {
        setPlantList(res.results);
      });
    }
  }, [zone.plant_type, zone.plant_name]);

  const sortedSensors = useMemo(() => {
    if (!zone.sensors?.length) return [];
    return [...(zone.sensors || [])]?.sort((a, b) => {
      const orderA = ZONE_SENSOR_TYPE_ORDER[a.sensor_type] ?? 999;
      const orderB = ZONE_SENSOR_TYPE_ORDER[b.sensor_type] ?? 999;
      return orderA - orderB;
    });
  }, [zone.sensors]);

  const activeMetricColumns = useMemo(() => {
    const sensors = zone?.sensors || [];

    return ZONE_METRIC_COLUMN_ORDER?.filter((measurementId) =>
      sensors?.some((sensor) => {
        const metric = getLatestMetric(sensor || {});

        if (!metric || typeof metric?.points === 'string') return false;
        return (metric?.points || [])?.some((p) => String(p.measurement_id) === measurementId);
      }),
    );
  }, [zone?.sensors]);

  const handleNavigate = () => {
    navigate(
      PAGES.ZONE_DETAILS.replace(':id', String(zone.id)) +
        (zone.is_deleted ? `?${QUERY_PARAMS.IS_DELETED}=${zone.is_deleted}` : ''),
    );
  };

  const plantName = useMemo(() => {
    return (
      (plantList || [])?.find(
        (plant) => plant?.value?.replace(/[_-]/g, '') === zone?.plant_name?.replace(/[_-]/g, ''),
      )?.label || '-'
    );
  }, [plantList, zone?.plant_name]);

  const data = useMemo(() => {
    return [
      { label: 'Plant type', value: formatSnakeCase(zone.plant_type) },
      { label: 'Plant name', value: plantName || '-' },
      {
        label: 'Soil type',
        value: SOIL_TYPE?.find((soil) => soil.value === zone.soil_type)?.label || '-',
      },
      { label: 'Sensors', value: zone.sensors?.length || 0 },
    ];
  }, [zone, plantName]);

  return (
    <div
      className={classNames('dashboard-zone-card', STATUS_TO_CONTAINER_CLASS[zoneStatus])}
      onClick={handleNavigate}
      style={{ cursor: 'pointer' }}
    >
      <div className={classNames('dashboard-zone-card__top')}>
        <div className={classNames('dashboard-zone-card__header')}>
          <div className={classNames('dashboard-zone-card__image')}>
            <img src={photoUrl || (zone.photo_file_id ? undefined : noImage)} alt={zone.name} />
          </div>

          <div className={classNames('dashboard-zone-card__header-text')}>
            <span className={classNames('dashboard-zone-card__name')}>{zone.name}</span>
            <Badge
              className="dashboard__badge"
              type={STATUS_TO_BADGE[zoneStatus]}
              title={zoneStatus}
            />
          </div>

          {zone.is_deleted && (
            <div className={classNames('zone-details__header-deleted')}>Deleted</div>
          )}
        </div>

        <div className={classNames('dashboard-zone-card__info')}>
          {(data || [])?.map((item) => (
            <div key={item.label} className={classNames('dashboard-zone-card__info-item')}>
              <span className={classNames('dashboard-zone-card__info-label')}>{item.label}</span>
              <span className={classNames('dashboard-zone-card__info-value')}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={classNames('dashboard-zone-card__body')}>
        {sortedSensors.length > 0 ? (
          <>
            <h3 className={classNames('dashboard-zone-card__table-title')}>Zone sensors</h3>
            <div className={classNames('dashboard-zone-card__table-wrapper')}>
              <table className={classNames('dashboard-zone-card__table')}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Battery</th>
                    {(activeMetricColumns || [])?.map((id) => (
                      <th key={id}>
                        {ZONE_METRIC_SHORT_LABELS[id] || SensorMeasurement[id]?.label || id}
                      </th>
                    ))}
                    <th>Last update</th>
                  </tr>
                </thead>

                <tbody>
                  {(sortedSensors || [])?.map((sensor) => {
                    const status = sensor.active
                      ? getSensorStatus(sensor)
                      : { text: '-', isOffline: false };
                    const battery = getSensorBattery(sensor);
                    const lastUpdate = getLastUpdate(sensor);
                    const isAlert = status.isOffline || battery.isLow;

                    return (
                      <tr
                        key={sensor.id}
                        className={classNames({
                          'row-alert': isAlert,
                          'row-online': !isAlert && status.text === 'Online',
                        })}
                      >
                        <td data-label="Type">{sensorTypeLabel(sensor.sensor_type)}</td>
                        <td data-label="Status">
                          <span
                            className={classNames({
                              'status-online': status.text === 'Online',
                              'status-offline':
                                status.text === 'Offline' || status.text === 'Turned off',
                            })}
                          >
                            {status.text}
                          </span>
                        </td>
                        <td data-label="Battery">
                          <Battery sensor={{ battery: battery.text }} />
                        </td>
                        {(activeMetricColumns || [])?.map((measurementId) => (
                          <td
                            key={measurementId}
                            data-label={ZONE_METRIC_SHORT_LABELS[measurementId] || measurementId}
                          >
                            {getMetricValue(sensor, measurementId)}
                          </td>
                        ))}
                        <td data-label="Last update">{lastUpdate}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className={classNames('dashboard-zone-card__empty')}>
            <h3 className={classNames('dashboard-zone-card__table-title')}>Zone sensors</h3>
            <div className={classNames('dashboard-zone-card__empty-content')}>
              <div className={classNames('dashboard-zone-card__empty-icon')}>
                <EmptySvg />
              </div>
              <span className={classNames('dashboard-zone-card__empty-text')}>No sensors yet</span>
            </div>
          </div>
        )}

        <Button
          className={classNames('dashboard-zone-card__btn')}
          variant="secondary"
          size="medium"
          fullWidth
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleNavigate();
          }}
        >
          View details
        </Button>
      </div>
    </div>
  );
};
